import invalidateCanvasConnections from '@/hooks/canvas/helpers/invalidateCanvasConnections';
import buildCanvasQueryKey from '@/hooks/canvas/helpers/buildCanvasQueryKey';
import type { SerializedCanvasConnection } from '@/hooks/canvas/types';
import coerceToRecord from '../helpers/coerceToRecord';
import eventMatchesToolNames from '../helpers/eventMatchesToolNames';
import getStringValue from '../helpers/getStringValue';
import type {
    BuildRefetchCanvasOnConnectionRemoveSubscriptionParams,
    ChatEffectSubscription,
} from '../types';

const CANVAS_CONNECTION_REMOVE_TOOL_NAMES = [
    'remove_canvas_connection',
    'canvases.removeConnection',
];

const buildRefetchCanvasOnConnectionRemoveSubscription = (
    _params: BuildRefetchCanvasOnConnectionRemoveSubscriptionParams
): ChatEffectSubscription => {
    return {
        id: 'refetch-canvas-on-connection-remove',
        routePattern: '/agents/:agentId/canvas/:canvasId',
        eventType: 'TOOL_CALL_RESULT',
        filter: ({ event, matchParams }) => {
            if (
                !eventMatchesToolNames({
                    event,
                    toolNames: CANVAS_CONNECTION_REMOVE_TOOL_NAMES,
                })
            ) {
                return false;
            }

            const args = coerceToRecord({ value: event.args });
            const canvasIdFromArgs = getStringValue({ value: args.canvasId });

            if (!canvasIdFromArgs) {
                return true;
            }

            return canvasIdFromArgs === matchParams.canvasId;
        },
        action: ({ event, queryClient, matchParams }) => {
            const args = coerceToRecord({ value: event.args });
            const connectionId = getStringValue({ value: args.connectionId });

            if (connectionId) {
                queryClient.setQueryData(
                    buildCanvasQueryKey({ scope: 'connections', canvasId: matchParams.canvasId }),
                    (existingConnections: SerializedCanvasConnection[] | undefined) => {
                        const safeExistingConnections = existingConnections ?? [];
                        return safeExistingConnections.filter(
                            existingConnection => existingConnection.connectionId !== connectionId
                        );
                    }
                );
            }

            invalidateCanvasConnections({
                queryClient,
                organizationId: matchParams.agentId,
                canvasId: matchParams.canvasId,
            });
        },
    };
};

export default buildRefetchCanvasOnConnectionRemoveSubscription;
